/**
 * @fileoverview Safe JSON parsing utilities with error tracking.
 * @module helpers/parser
 */

import * as Sentry from "@sentry/browser";

/**
 * Safely parses a JSON string, returning the original value if parsing fails.
 *
 * If the input is already an object, returns it unchanged.
 * If parsing fails, logs the error to Sentry and returns the original string.
 *
 * @param {Object|string} object - The value to parse (string or object)
 * @returns {Object|string} Parsed object if successful, original value otherwise
 *
 * @example
 * // Parse a JSON string
 * jsonParse('{"name": "John"}'); // Returns: { name: 'John' }
 *
 * // Already an object - returns unchanged
 * jsonParse({ name: 'John' }); // Returns: { name: 'John' }
 *
 * // Invalid JSON - returns original string
 * jsonParse('not valid json'); // Returns: 'not valid json'
 */
export function jsonParse(object: object | string): object | string {
  try {
    if (!object || object === "null" || object === "") {
      return object; // common legacy/empty cases, no need to treat as error
    }
    let parsedObject = object;
    if (typeof object === "string") {
      parsedObject = JSON.parse(object);
    }

    return parsedObject;
  } catch (err) {
    // Only report unexpected parse failures (e.g. corrupted data); many "errors" here were from empty/null legacy values.
    // Still return original to be safe.
    if (object && typeof object === "string" && object.length > 2) {
      Sentry.captureException(err);
    }
    return object;
  }
}
